import { useEffect } from "react";
import { Link } from "react-router";
import { differenceInCalendarDays, format, startOfDay } from "date-fns";
import { GraduationCap, PartyPopper } from "lucide-react";
import { OnboardingBannerColor, Permission } from "@forgepoint/shared";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { useOnboardingStore } from "./onboardingStore";
import { parseDateOnly } from "./onboardingDates";

/**
 * Tailwind tone for each admin-selectable banner color, applied while tasks are
 * still open. Full literal class strings so Tailwind's scanner keeps them. The
 * text token is whichever reads on the `/10` tint: `-foreground` for amber/blue
 * (their base token is too light on the tint), base for red/brand (their
 * `-foreground` token is near-white).
 */
const BANNER_TONE: Record<OnboardingBannerColor, string> = {
  WARNING: "border-warning/40 bg-warning/10 text-warning-foreground",
  DESTRUCTIVE: "border-destructive/40 bg-destructive/10 text-destructive",
  INFO: "border-info/40 bg-info/10 text-info-foreground",
  PRIMARY: "border-primary/40 bg-primary/10 text-primary",
};

/**
 * Persistent app-shell strip shown while the organization's onboarding plan
 * is active. Links to the onboarding tab, shows a countdown inside three days
 * of go-live, and flips to a celebration state once every task is complete.
 * Deliberately not dismissible.
 */
export function OnboardingBanner() {
  const hasUser = useAuthStore((state) => Boolean(state.user));
  const canViewOnboarding = useHasPermission(Permission.ONBOARDING_VIEW);
  const summary = useOnboardingStore((state) => state.summary);
  const ensureSummary = useOnboardingStore((state) => state.ensureSummary);
  const agentName = useStandardAgentDisplayName();

  useEffect(() => {
    if (hasUser && canViewOnboarding) {
      ensureSummary();
    }
  }, [hasUser, canViewOnboarding, ensureSummary]);

  if (!canViewOnboarding || !summary?.plan) {
    return null;
  }

  const { goLiveDate, bannerMessage, bannerColor } = summary.plan;
  const goLive = goLiveDate ? parseDateOnly(goLiveDate) : null;
  const daysToGoLive = goLive
    ? differenceInCalendarDays(goLive, startOfDay(new Date()))
    : null;
  const allTasksDone =
    summary.totalTaskCount > 0 && summary.openTaskCount === 0;
  const isUrgent =
    !allTasksDone &&
    daysToGoLive !== null &&
    daysToGoLive >= 0 &&
    daysToGoLive <= 3;

  const toneClass = allTasksDone
    ? "border-success/40 bg-success/10 text-success"
    : BANNER_TONE[bannerColor];

  const message = allTasksDone
    ? `Go-live confirmed 🎉${goLive ? ` — see you ${format(goLive, "EEE, MMM d")}.` : ""}`
    : (bannerMessage ??
      `${agentName} is still in training. Please complete your onboarding tasks${
        goLive ? ` — go-live is ${format(goLive, "EEE, MMM d")}` : ""
      }.`);

  return (
    <Link
      to="/onboarding"
      className={`flex shrink-0 items-center gap-2 border-b px-4 py-2 text-sm transition-colors hover:brightness-95 ${toneClass}`}
    >
      {allTasksDone ? (
        <PartyPopper className="h-4 w-4 shrink-0" />
      ) : (
        <GraduationCap className="h-4 w-4 shrink-0" />
      )}
      <span className="min-w-0 truncate">{message}</span>
      <span className="ml-auto flex shrink-0 items-center gap-3 text-xs">
        {!allTasksDone && summary.openTaskCount > 0 && (
          <span>
            {summary.openTaskCount} open task
            {summary.openTaskCount === 1 ? "" : "s"}
          </span>
        )}
        {isUrgent && daysToGoLive !== null && (
          <span className="font-semibold">
            {daysToGoLive === 0
              ? "Go-live today"
              : `${daysToGoLive} day${daysToGoLive === 1 ? "" : "s"} left`}
          </span>
        )}
      </span>
    </Link>
  );
}
